import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useBlocker } from 'react-router-dom'
import { toast } from 'sonner'
import {
  DEFAULT_TRANSFER_MESSAGE,
  type NodeActionGroup,
  type SaveNodeActionDto,
  type SaveNodeActionGroupDto,
  type SaveWorkflowActionDto,
  type Integration,
} from '@super/shared-types'
import {
  ActionCatalogForm,
  validateActionCatalogForm,
} from '@/components/actions/ActionCatalogForm'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useAppAuth } from '@/contexts/app-auth.context'
import { useDirectory } from '@/contexts/directory.context'
import { actionsCatalogApiClient } from '@/lib/api/actions-catalog.client'
import { actionsApiClient } from '@/lib/api/actions.client'
import { integrationsClient } from '@/lib/api/integrations/integrations.client'
import {
  mapStaffUserToItem,
  mapTaskCategoryToItem,
  mapVendorToItem,
} from '@/lib/api/integrations/buildium.utils'
import { mapAssigneeToItem } from '@/lib/api/integrations/hubspot.utils'
import {
  type ActionRow,
  type ActionRowType,
  type PushFieldDropdownItem,
  type PushOption,
  type TransferSettings,
} from '@/components/actions/ActionEditor'
import { buildPushOptions } from '@/lib/handoff-rule.converters'
import {
  useDropdownSourceFetching,
  type DropdownSourceFetchers,
} from '@/pages/actions/useDropdownSourceFetching'
import {
  buildCatalogDtosForRow,
  hydrateMembersToRowStates,
  type RowState as UtilRowState,
} from './node-agent-action-row-utils'

const DEFAULT_TRANSFER: TransferSettings = {
  connectionMethod: 'SIMPLE_TRANSFER',
  preserveOriginalCallerId: false,
  declinedMessage: null,
  transferMessage: DEFAULT_TRANSFER_MESSAGE,
  voicemailDetectionEnabled: true,
  leaveVoicemailEnabled: true,
  voicemailMessage: null,
}

const newId = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `id-${Math.random().toString(36).slice(2, 10)}`

type RowState = UtilRowState

interface NodeAgentActionInspectorProps {
  nodeActionGroupId: string
  /** Null in draft mode — the author clicked "Create new action" from the
   *  picker and no POST has happened yet. The inspector synthesizes a blank
   *  group and saves via `onGroupCreated` instead of PUT. */
  group: NodeActionGroup | null
  isDraft: boolean
  onGroupUpdated: (group: NodeActionGroup) => void
  /** Fires after the first save of a draft lands server-side. The parent
   *  seeds the created group into its caches and swaps the attached id. */
  onGroupCreated: (group: NodeActionGroup) => void
  replaceGroupId: (newGroupId: string) => void
  /** Lifts save state so the parent can render the sticky save CTA in the
   *  shared edit shell's footer (parity with TransferNodePanel). */
  onSaveStateChange?: (state: {
    isDirty: boolean
    saving: boolean
    save: () => void
    /** Run the same field-level validation Save does, but without POSTing or
     *  PUTting. The parent calls this when the graph-save gate detects an
     *  invalid attached group so the open inspector highlights the fields
     *  the author must fix. */
    validate: () => boolean
  }) => void
  /** When true, the inspector does not draw its own save row. Parent is
   *  rendering the save button through the shell footer instead. */
  hideSaveRow?: boolean
}

const DRAFT_GROUP: NodeActionGroup = {
  id: 'draft',
  companyId: '',
  name: null,
  description: null,
  members: [],
  updatedBy: null,
  createdAt: '',
  updatedAt: '',
} as NodeActionGroup

/** Inspector body for an action node. Mounts the same ActionRowEditor the
 *  Actions page uses. Shared groups show a warning banner + a duplicate-or-
 *  update prompt on the first edit so the author never silently changes an
 *  action referenced by other graphs. */
export function NodeAgentActionInspector({
  nodeActionGroupId,
  group: inputGroup,
  isDraft,
  onGroupUpdated,
  onGroupCreated,
  onSaveStateChange,
  hideSaveRow,
}: NodeAgentActionInspectorProps) {
  const { isSuperAdmin, currentCompany } = useAppAuth()
  const { roles, staff } = useDirectory()
  // In draft mode the panel passes `group=null`; the inspector synthesizes
  // a blank group so every downstream hook (hydratedRows, pushOptions keyed
  // by group.id, etc.) sees a stable shape. The sentinel id is never sent
  // to the server — persist branches on `isDraft` to call POST instead.
  const group = inputGroup ?? DRAFT_GROUP

  const [availableIntegrations, setAvailableIntegrations] = useState<
    Integration[]
  >([])
  const [integrationsLoading, setIntegrationsLoading] = useState(true)
  const pushOptions = useMemo<PushOption[]>(
    () => buildPushOptions(availableIntegrations),
    [availableIntegrations],
  )

  // Integrations populate the PUSH_INTEGRATION target picker. A blank draft
  // has no rows yet, so defer the fetch until the author adds one — keeps
  // the "Create new action" click truly network-free.
  const hasRows = (group.members ?? []).length > 0 || !isDraft
  useEffect(() => {
    const companyId = currentCompany?.id
    if (!companyId || !hasRows) {
      setIntegrationsLoading(false)
      return
    }
    let cancelled = false
    void (async () => {
      try {
        setIntegrationsLoading(true)
        const integrations = isSuperAdmin
          ? await integrationsClient.getPushEnabledIntegrationsAdmin(companyId)
          : await integrationsClient.getPushEnabledIntegrations()
        if (cancelled) return
        setAvailableIntegrations(integrations)
      } catch (error) {
        console.error('Failed to load integrations', error)
      } finally {
        if (!cancelled) setIntegrationsLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [currentCompany?.id, hasRows, isSuperAdmin])

  const dropdownSourceFetchers: DropdownSourceFetchers = useMemo(() => {
    const stripData = (
      items: ({ id: string; label: string; sublabel?: string } | null)[],
    ): PushFieldDropdownItem[] =>
      items
        .filter(
          (item): item is { id: string; label: string; sublabel?: string } =>
            item !== null,
        )
        .map(item => ({
          id: item.id,
          label: item.label,
          sublabel: item.sublabel,
        }))

    let pipelinesPromise: ReturnType<
      typeof integrationsClient.hubspot.getTicketPipelines
    > | null = null
    const fetchTicketPipelines = () => {
      pipelinesPromise ??= (
        isSuperAdmin && currentCompany
          ? integrationsClient.hubspot.getAdminTicketPipelines(
              currentCompany.id,
            )
          : integrationsClient.hubspot.getTicketPipelines()
      ).catch(error => {
        pipelinesPromise = null
        throw error
      })
      return pipelinesPromise
    }

    return {
      'buildium-task-categories': async () => {
        const categories =
          isSuperAdmin && currentCompany
            ? await integrationsClient.buildium.getAdminTaskCategories(
                currentCompany.id,
              )
            : await integrationsClient.buildium.getTaskCategories()
        return stripData(categories.map(mapTaskCategoryToItem))
      },
      'buildium-staff-users': async () => {
        const users =
          isSuperAdmin && currentCompany
            ? await integrationsClient.buildium.getAdminStaffUsers(
                currentCompany.id,
              )
            : await integrationsClient.buildium.getStaffUsers()
        return stripData(users.map(mapStaffUserToItem))
      },
      'buildium-vendors': async () => {
        const vendors =
          isSuperAdmin && currentCompany
            ? await integrationsClient.buildium.getAdminVendors(
                currentCompany.id,
              )
            : await integrationsClient.buildium.getVendors()
        return stripData(vendors.map(mapVendorToItem))
      },
      'hubspot-owners': async () => {
        const owners =
          isSuperAdmin && currentCompany
            ? await integrationsClient.hubspot.getAdminAssignees(
                currentCompany.id,
              )
            : await integrationsClient.hubspot.getAssignees()
        return stripData(owners.map(mapAssigneeToItem))
      },
      'hubspot-ticket-pipelines': async () => {
        const pipelines = await fetchTicketPipelines()
        return pipelines.map(p => ({ id: p.id, label: p.label }))
      },
      'hubspot-ticket-stages': async () => {
        const pipelines = await fetchTicketPipelines()
        return pipelines.flatMap(p =>
          p.stages.map(s => ({
            id: s.id,
            label: s.label,
            sublabel: p.label,
            groupId: p.id,
          })),
        )
      },
      'doorloop-users': async () => {
        const users =
          isSuperAdmin && currentCompany
            ? await integrationsClient.doorLoop.getAdminUsers(currentCompany.id)
            : await integrationsClient.doorLoop.getUsers()
        return stripData(users.map(mapAssigneeToItem))
      },
      'doorloop-vendors': async () => {
        const vendors =
          isSuperAdmin && currentCompany
            ? await integrationsClient.doorLoop.getAdminVendors(
                currentCompany.id,
              )
            : await integrationsClient.doorLoop.getVendors()
        return stripData(vendors.map(mapAssigneeToItem))
      },
    }
  }, [isSuperAdmin, currentCompany])

  const hydratedRows = useMemo<RowState[]>(
    () => hydrateMembersToRowStates(group, { pushOptions, roles, staff }),
    [group, pushOptions, roles, staff],
  )

  // Draft mode opens with one blank EMAIL row — the author has something to
  // type into instead of needing an "Add action" click first.
  const [rows, setRows] = useState<RowState[]>(() =>
    hydratedRows.length > 0 || !isDraft
      ? hydratedRows
      : [
          {
            localId: newId(),
            workflowActionId: null,
            row: { id: newId(), type: 'EMAIL', target: '' },
          },
        ],
  )
  const [transfer, setTransfer] = useState<TransferSettings>(() =>
    hydratedRows[0]?.row.type === 'TRANSFER'
      ? (extractTransferSettings(group) ?? DEFAULT_TRANSFER)
      : DEFAULT_TRANSFER,
  )

  // Re-hydrate when the group changes server-side. Only replace when the
  // local draft matches the incoming server shape — if the author has
  // unsaved edits (e.g. an external cause triggered a refetch mid-type),
  // keep the draft and skip the replace so no typing gets wiped.
  const lastHydratedKeyRef = useRef<string>('')
  const rowsRef = useRef(rows)
  rowsRef.current = rows
  useEffect(() => {
    const key = JSON.stringify({
      groupId: group.id,
      rows: hydratedRows,
    })
    if (key === lastHydratedKeyRef.current) return
    lastHydratedKeyRef.current = key
    const localRowKey = JSON.stringify(rowsRef.current.map(r => r.row))
    const serverRowKey = JSON.stringify(hydratedRows.map(r => r.row))
    if (localRowKey !== serverRowKey) return
    setRows(hydratedRows)
    setTransfer(extractTransferSettings(group) ?? DEFAULT_TRANSFER)
  }, [group, hydratedRows])

  const syntheticGroups = useMemo(
    () => [
      {
        id: group.id,
        name: '',
        when: '',
        actions: rows.map(r => r.row),
        transfer: rows.some(r => r.row.type === 'TRANSFER')
          ? transfer
          : undefined,
      },
    ],
    [group.id, rows, transfer],
  )
  const { dropdownItems, failedSources } = useDropdownSourceFetching(
    syntheticGroups,
    pushOptions,
    dropdownSourceFetchers,
    integrationsLoading,
  )

  const [saving, setSaving] = useState(false)
  const [name, setName] = useState(group.name ?? '')
  const [description, setDescription] = useState(group.description ?? '')
  const [nameError, setNameError] = useState(false)
  const [targetErrors, setTargetErrors] = useState<ReadonlySet<string>>(
    () => new Set(),
  )
  const nameInputRef = useRef<HTMLInputElement | null>(null)
  const targetSectionRef = useRef<HTMLDivElement | null>(null)

  const focusFirstInvalid = useCallback(
    (failedNameError: boolean, failedTargetErrors: ReadonlySet<string>) => {
      if (failedNameError) {
        nameInputRef.current?.focus()
        return
      }
      if (failedTargetErrors.size === 0) return
      const section = targetSectionRef.current
      if (!section) return
      const firstRowId = rows.find(r => failedTargetErrors.has(r.row.id))?.row
        .id
      if (!firstRowId) return
      // The row is keyed by row.id — rely on the ActionRowEditor's rendered
      // input/textarea order and scroll to the first invalid row's section.
      const rowEl = section.children[
        rows.findIndex(r => r.row.id === firstRowId)
      ] as HTMLElement | undefined
      rowEl?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      rowEl
        ?.querySelector<
          HTMLInputElement | HTMLTextAreaElement
        >('input,textarea')
        ?.focus()
    },
    [rows],
  )
  // Re-seed Name/Description when the attached group id changes (Change /
  // Duplicate / picker swap). Lets a fresh group hydrate cleanly.
  useEffect(() => {
    setName(group.name ?? '')
    setDescription(group.description ?? '')
    setNameError(false)
    setTargetErrors(new Set())
  }, [group.id, group.name, group.description])

  const buildWorkflowDtos = useCallback(
    (nextRow: ActionRow, nextTransfer: TransferSettings) =>
      buildCatalogDtosForRow(nextRow, nextTransfer, {
        groupId: group.id,
        roles,
        staff,
        availableIntegrations,
      }),
    [availableIntegrations, group.id, roles, staff],
  )

  const scopedCatalogSave = useCallback(
    async (
      existingId: string | null,
      dtos: SaveWorkflowActionDto[],
    ): Promise<{ ids: string[] } | null> => {
      if (dtos.length === 0) return { ids: [] }
      const payload = {
        existingId: existingId ?? undefined,
        actions: dtos,
      }
      const result =
        isSuperAdmin && currentCompany
          ? await actionsCatalogApiClient.bulkSaveForCompany(
              currentCompany.id,
              payload,
            )
          : await actionsCatalogApiClient.bulkSave(payload)
      if (!result || result.length === 0) return null
      return { ids: result.map(r => r.id) }
    },
    [currentCompany, isSuperAdmin],
  )

  const scopedGroupPut = useCallback(
    async (
      targetGroupId: string,
      actions: SaveNodeActionDto[],
    ): Promise<NodeActionGroup | null> => {
      // Prefer the author's local edits over the hydrated group values.
      // For inline (unnamed) groups the backend accepts null; the inspector
      // trims blank entries back to null so a cleared field doesn't persist
      // as an empty string.
      const dto: SaveNodeActionGroupDto = {
        name: name.trim() ? name.trim() : null,
        description: description.trim() ? description.trim() : null,
        actions,
      }
      return isSuperAdmin && currentCompany
        ? actionsApiClient.updateActionForCompany(
            targetGroupId,
            currentCompany.id,
            dto,
          )
        : actionsApiClient.updateAction(targetGroupId, dto)
    },
    [currentCompany, description, isSuperAdmin, name],
  )

  const refetchGroup = useCallback(
    async (id: string): Promise<NodeActionGroup | null> => {
      return isSuperAdmin && currentCompany
        ? actionsApiClient.getActionForCompanyByAdmin(id, currentCompany.id)
        : actionsApiClient.getActionById(id)
    },
    [currentCompany, isSuperAdmin],
  )

  const persist = useCallback(
    async (
      targetGroupId: string,
      nextRows: RowState[],
      nextTransfer: TransferSettings,
    ) => {
      // Two-phase save, ordered so a mid-flight failure can't leak
      // orphaned WorkflowAction rows.
      //
      // Phase 1: update rows that ALREADY exist in the catalog. These
      // writes mutate rows in place, so a failure or an abort can't leave
      // dangling rows — the WorkflowActions are still attached to this
      // group through their existing members.
      //
      // Phase 2: rewrite the group's member list in one transaction.
      // Rows the author just added go in as `kind: inline` so the DAO
      // creates the WorkflowAction + ActionContact rows + member link
      // atomically. A PUT failure rolls back the inline creates, so no
      // orphans ever reach the catalog.
      const actions: SaveNodeActionDto[] = []
      for (const r of nextRows) {
        const { dtos } = buildWorkflowDtos(r.row, nextTransfer)
        if (dtos.length === 0) continue
        if (r.workflowActionId) {
          // Preserve the existing catalog row's name + description; the
          // inspector only edits body fields, so a save that omits them would
          // overwrite the stored name to null and the row would drop out of
          // the catalog's named-only listing.
          const existing = (group.members ?? []).find(
            m => m.workflowActionId === r.workflowActionId,
          )?.workflowAction
          const dtosForSave = dtos.map((d, i) =>
            i === 0
              ? {
                  ...d,
                  name: existing?.name ?? null,
                  description: existing?.description ?? null,
                }
              : d,
          )
          const saved = await scopedCatalogSave(r.workflowActionId, dtosForSave)
          if (!saved) {
            toast.error('Save failed')
            return false
          }
          for (const id of saved.ids) {
            actions.push({
              workflowActionId: id,
              actionType: dtos[0].actionType,
            })
          }
        } else {
          for (const dto of dtos) {
            actions.push({
              actionType: dto.actionType,
              actionSubtype: dto.actionSubtype ?? null,
              parameters: dto.parameters ?? null,
              integrationId: dto.integrationId ?? null,
              contacts: dto.contacts,
            })
          }
        }
      }
      const memberIdsBefore = (group.members ?? []).map(m => m.workflowActionId)
      const memberIdsAfter = actions
        .map(a => a.workflowActionId)
        .filter((id): id is string => !!id)
      const addedInline = actions.some(a => !a.workflowActionId)
      const membershipChanged =
        targetGroupId !== nodeActionGroupId ||
        addedInline ||
        memberIdsBefore.length !== memberIdsAfter.length ||
        memberIdsBefore.some((id, i) => id !== memberIdsAfter[i])
      if (membershipChanged) {
        const updated = await scopedGroupPut(targetGroupId, actions)
        if (!updated) {
          toast.error('Could not update the action group')
          return false
        }
        onGroupUpdated(updated)
      } else {
        const refreshed = await refetchGroup(targetGroupId)
        if (refreshed) onGroupUpdated(refreshed)
      }
      return true
    },
    [
      buildWorkflowDtos,
      group.members,
      nodeActionGroupId,
      onGroupUpdated,
      refetchGroup,
      scopedCatalogSave,
      scopedGroupPut,
    ],
  )

  // Local-only mutations — no network. The author batches edits + commits
  // through the Save button.
  type Mutation =
    | { kind: 'add'; type: ActionRowType }
    | { kind: 'remove'; localId: string }
    | { kind: 'edit'; localId: string; patch: Partial<ActionRow> }

  const applyMutation = useCallback((m: Mutation) => {
    if (m.kind === 'add') {
      setRows(prev => [
        ...prev,
        {
          localId: newId(),
          workflowActionId: null,
          row: { id: newId(), type: m.type, target: '' },
        },
      ])
      return
    }
    if (m.kind === 'remove') {
      setRows(prev => prev.filter(r => r.localId !== m.localId))
      return
    }
    setRows(prev =>
      prev.map(r =>
        r.localId === m.localId
          ? ({
              ...r,
              row: { ...r.row, ...m.patch } as ActionRow,
            } as RowState)
          : r,
      ),
    )
  }, [])

  const handleRowChange = (localId: string, patch: Partial<ActionRow>) =>
    applyMutation({ kind: 'edit', localId, patch })
  const handleRemoveRow = (localId: string) =>
    applyMutation({ kind: 'remove', localId })
  const handleAppendRow = () => {
    const existingTypes = new Set(rows.map(r => r.row.type))
    const nextType =
      (
        ['EMAIL', 'SMS', 'TRANSFER', 'PUSH_INTEGRATION'] as ActionRowType[]
      ).find(t => !existingTypes.has(t)) ?? 'EMAIL'
    applyMutation({ kind: 'add', type: nextType })
  }

  // Dirty check: compare the local editor state against the server-hydrated
  // state. Covers row contents, add/remove, reorder, and transfer settings.
  const hydratedTransfer = useMemo(
    () => extractTransferSettings(group) ?? DEFAULT_TRANSFER,
    [group],
  )
  const isDirty = useMemo(() => {
    if (isDraft) return true
    const base = JSON.stringify({
      name: group.name ?? '',
      description: group.description ?? '',
      rows: hydratedRows.map(r => ({
        workflowActionId: r.workflowActionId,
        row: r.row,
      })),
      transfer: hydratedTransfer,
    })
    const current = JSON.stringify({
      name,
      description,
      rows: rows.map(r => ({
        workflowActionId: r.workflowActionId,
        row: r.row,
      })),
      transfer,
    })
    return base !== current
  }, [
    description,
    group.description,
    group.name,
    hydratedRows,
    hydratedTransfer,
    isDraft,
    name,
    rows,
    transfer,
  ])

  const runSaveDirect = useCallback(async () => {
    const result = validateActionCatalogForm({
      name,
      rows: rows.map(r => r.row),
    })
    setNameError(result.nameError)
    setTargetErrors(result.targetErrors)
    const issues = [...result.issues]
    if (isDraft && rows.length === 0) {
      issues.push('Add at least one action')
    }
    if (issues.length > 0) {
      toast.error(issues.join(' · '))
      focusFirstInvalid(result.nameError, result.targetErrors)
      return
    }
    setSaving(true)
    try {
      if (isDraft) {
        const actions: SaveNodeActionDto[] = []
        for (const r of rows) {
          const { dtos } = buildWorkflowDtos(r.row, transfer)
          for (const dto of dtos) {
            actions.push({
              actionType: dto.actionType,
              actionSubtype: dto.actionSubtype ?? null,
              parameters: dto.parameters ?? null,
              integrationId: dto.integrationId ?? null,
              contacts: dto.contacts,
            })
          }
        }
        const groupDto: SaveNodeActionGroupDto = {
          name: name.trim() ? name.trim() : null,
          description: description.trim() ? description.trim() : null,
          actions,
        }
        const created =
          isSuperAdmin && currentCompany
            ? await actionsApiClient.createActionForCompany(
                currentCompany.id,
                groupDto,
              )
            : await actionsApiClient.createAction(groupDto)
        if (!created) {
          toast.error('Could not create the action')
          return
        }
        onGroupCreated(created)
        toast.success('Action saved')
        return
      }
      const ok = await persist(nodeActionGroupId, rows, transfer)
      if (ok) toast.success('Action saved')
    } finally {
      setSaving(false)
    }
  }, [
    buildWorkflowDtos,
    currentCompany,
    description,
    group.name,
    isDraft,
    isSuperAdmin,
    name,
    nodeActionGroupId,
    onGroupCreated,
    persist,
    rows,
    transfer,
    focusFirstInvalid,
  ])

  // Stable identity so the lifted-state effect only runs when values change.
  // The parent re-renders on every save-state push and passes fresh arrow
  // callbacks (onGroupUpdated, etc.) that bubble through persist → runSaveDirect
  // → handleSave; letting those identity-churn through to the lifted state
  // caused an infinite setState loop.
  const saveHandlerRef = useRef<() => void>(() => {})
  useEffect(() => {
    saveHandlerRef.current = () => {
      if (!isDirty || saving) return
      void runSaveDirect()
    }
  }, [isDirty, saving, runSaveDirect])
  const stableHandleSave = useCallback(() => saveHandlerRef.current(), [])

  // Validation-only sibling to handleSave. Highlights fields the same way
  // Save does but never POSTs/PUTs — used by the graph save gate when the
  // inspector is already open on the invalid node.
  const validateHandlerRef = useRef<() => boolean>(() => true)
  useEffect(() => {
    validateHandlerRef.current = () => {
      const result = validateActionCatalogForm({
        name,
        rows: rows.map(r => r.row),
      })
      setNameError(result.nameError)
      setTargetErrors(result.targetErrors)
      const issues = [...result.issues]
      if (isDraft && rows.length === 0) issues.push('Add at least one action')
      if (issues.length > 0) {
        focusFirstInvalid(result.nameError, result.targetErrors)
      }
      return issues.length === 0
    }
  }, [focusFirstInvalid, isDraft, name, rows])
  const stableValidate = useCallback(() => validateHandlerRef.current(), [])

  const onSaveStateChangeRef = useRef(onSaveStateChange)
  useEffect(() => {
    onSaveStateChangeRef.current = onSaveStateChange
  }, [onSaveStateChange])
  useEffect(() => {
    onSaveStateChangeRef.current?.({
      isDirty,
      saving,
      save: stableHandleSave,
      validate: stableValidate,
    })
  }, [isDirty, saving, stableHandleSave, stableValidate])

  // ── Unsaved-changes guard (in-app navigation + hard refresh/close) ───────
  // The author's draft lives in local state only until Save fires. Without
  // this guard, a stray back-button click or sidebar nav silently discards
  // every pending edit.
  const bypassGuard = useRef(false)
  const blocker = useBlocker(
    useCallback(
      ({
        currentLocation,
        nextLocation,
      }: {
        currentLocation: { pathname: string }
        nextLocation: { pathname: string }
      }) =>
        isDirty &&
        !saving &&
        !bypassGuard.current &&
        currentLocation.pathname !== nextLocation.pathname,
      [isDirty, saving],
    ),
  )
  useEffect(() => {
    if (!isDirty) return
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault()
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [isDirty])

  return (
    <div className="space-y-4">
      <ActionCatalogForm
        actionGroupId={group.id}
        name={name}
        description={description}
        rows={rows.map(r => r.row)}
        transfer={transfer}
        onNameChange={next => {
          setName(next)
          if (next.trim()) setNameError(false)
        }}
        onDescriptionChange={setDescription}
        onRowChange={(rowId, patch) => {
          const localId = rows.find(r => r.row.id === rowId)?.localId
          if (!localId) return
          if (targetErrors.has(rowId)) {
            setTargetErrors(prev => {
              const next = new Set(prev)
              next.delete(rowId)
              return next
            })
          }
          handleRowChange(localId, patch)
        }}
        onRowAppend={handleAppendRow}
        onRowRemove={rowId => {
          const localId = rows.find(r => r.row.id === rowId)?.localId
          if (!localId) return
          handleRemoveRow(localId)
        }}
        nameError={nameError}
        targetErrors={targetErrors}
        disallowedTypesBase={new Set<ActionRowType>(['PHONE_CALL', 'TRANSFER'])}
        canAddRows
        isPanel
        pushOptions={pushOptions}
        pushFieldDropdownItems={dropdownItems}
        pushFieldFailedSources={failedSources}
        autoFocusName
        nameInputRef={nameInputRef}
        targetSectionRef={targetSectionRef}
      />

      {!hideSaveRow && (
        <div className="flex items-center justify-end pt-1">
          <Button
            type="button"
            size="sm"
            onClick={stableHandleSave}
            disabled={!isDirty || saving}
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      )}

      {blocker.state === 'blocked' && (
        <Dialog open onOpenChange={() => blocker.reset?.()}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Discard unsaved action changes?</DialogTitle>
              <DialogDescription>
                You've edited this action but haven't saved. If you leave now,
                your changes will be lost.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="ghost" onClick={() => blocker.reset?.()}>
                Stay
              </Button>
              <Button variant="destructive" onClick={() => blocker.proceed?.()}>
                Leave without saving
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  )
}

function extractTransferSettings(
  group: NodeActionGroup,
): TransferSettings | null {
  const transferRow = group.members.find(
    m => m.workflowAction.actionType === 'TRANSFER',
  )
  if (!transferRow) return null
  const params = (transferRow.workflowAction.parameters ?? {}) as Record<
    string,
    unknown
  >
  return {
    connectionMethod:
      (params.connectionMethod as TransferSettings['connectionMethod']) ??
      'SIMPLE_TRANSFER',
    preserveOriginalCallerId:
      (params.preserveOriginalCallerId as boolean | undefined) ?? false,
    declinedMessage:
      (params.declinedMessage as string | null | undefined) ?? null,
    transferMessage:
      (params.transferMessage as string | null | undefined) ?? null,
    voicemailDetectionEnabled:
      (params.voicemailDetectionEnabled as boolean | undefined) ?? true,
    leaveVoicemailEnabled:
      (params.leaveVoicemailEnabled as boolean | undefined) ?? true,
    voicemailMessage:
      (params.voicemailMessage as string | null | undefined) ?? null,
  }
}
